import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Shield, Users, BookOpen, ScrollText, Search, X, Trash2, Plus, Save, Pencil, Crown, Coins, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { getClass, levelFromXp } from "@/lib/rpg";
import { card, label, btnPrimary, btnSecondary, btnGhost, input, textarea, tag, tagHi } from "@/lib/styles";

const LEVELS = ["intro", "core", "advanced"];

function Modal({ onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className={`${card} ${wide ? "max-w-3xl" : "max-w-xl"} w-full my-8 p-6`} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-zinc-500 hover:text-zinc-200" data-testid="modal-close"><X size={18} /></button>
        {children}
      </div>
    </div>
  );
}

// ---------------- USERS ----------------
function UserDetail({ userId, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/admin/users/${userId}`).then((r) => {
      setD(r.data);
      const u = r.data.user;
      setForm({ plan: u.plan, gold: u.gold ?? 0, xp: u.xp ?? 0, trial_interviews_used: u.trial_interviews_used ?? 0, character_class: u.character_class || "", is_admin: !!u.is_admin, subscription_status: u.subscription_status || "" });
    }).catch(() => toast.error("Could not load user"));
  }, [userId]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, character_class: form.character_class || null, subscription_status: form.subscription_status || null };
      await api.patch(`/admin/users/${userId}`, payload);
      toast.success("User updated");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this user and all their data? This cannot be undone.")) return;
    try { await api.delete(`/admin/users/${userId}`); toast.success("User deleted"); onChanged?.(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  if (!d) return <Modal onClose={onClose}><p className="text-gold font-mono"><span className="cursor-blink">loading</span></p></Modal>;
  const u = d.user;
  const lvl = levelFromXp(u.xp || 0);

  return (
    <Modal onClose={onClose} wide>
      <span className={label}>user · {u.email}</span>
      <h3 className="font-serif font-bold text-2xl text-zinc-100 mt-1">{u.name}</h3>
      <p className="text-xs text-zinc-500 mt-1">{u.user_id} · joined {u.created_at?.slice(0, 10)} · {lvl.rank} Lv {lvl.level}</p>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5">
        <Field l="Plan"><select data-testid="edit-plan" className={input} value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}><option value="free">free</option><option value="pro">pro</option></select></Field>
        <Field l="Gold"><input data-testid="edit-gold" type="number" className={input} value={form.gold} onChange={(e) => setForm({ ...form, gold: +e.target.value })} /></Field>
        <Field l="XP"><input data-testid="edit-xp" type="number" className={input} value={form.xp} onChange={(e) => setForm({ ...form, xp: +e.target.value })} /></Field>
        <Field l="Trial used"><input type="number" className={input} value={form.trial_interviews_used} onChange={(e) => setForm({ ...form, trial_interviews_used: +e.target.value })} /></Field>
        <Field l="Class"><select className={input} value={form.character_class} onChange={(e) => setForm({ ...form, character_class: e.target.value })}><option value="">none</option><option value="sage">sage</option><option value="retriever">retriever</option><option value="architect">architect</option><option value="alchemist">alchemist</option></select></Field>
        <Field l="Sub status"><input className={input} value={form.subscription_status} onChange={(e) => setForm({ ...form, subscription_status: e.target.value })} /></Field>
      </div>
      <label className="flex items-center gap-2 mt-3 text-sm text-zinc-300"><input type="checkbox" checked={form.is_admin} onChange={(e) => setForm({ ...form, is_admin: e.target.checked })} /> Admin rights</label>

      <div className="flex gap-3 mt-5">
        <button data-testid="save-user" onClick={save} disabled={saving} className={btnPrimary}><Save size={15} /> {saving ? "saving..." : "Save"}</button>
        <button data-testid="delete-user" onClick={del} className={btnSecondary}><Trash2 size={15} /> Delete</button>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Panel title={`Interviews (${d.sessions.length})`}>
          {d.sessions.length === 0 ? <Empty /> : d.sessions.map((s) => (
            <div key={s.session_id} className="text-xs border-b border-zinc-800 py-2">
              <p className="text-zinc-300">{s.topic_title}</p>
              <p className="text-zinc-600">T{s.tier} · {s.started_at?.slice(0, 16).replace("T", " ")}</p>
            </div>
          ))}
        </Panel>
        <Panel title={`Graded answers (${d.turns.filter(t => t.score != null).length})`}>
          {d.turns.filter(t => t.score != null).length === 0 ? <Empty /> : d.turns.filter(t => t.score != null).map((t) => (
            <div key={t.turn_id} className="text-xs border-b border-zinc-800 py-2">
              <p className="text-zinc-300 truncate">{t.question}</p>
              <p className="text-gold">score {t.score} · {t.verdict}</p>
            </div>
          ))}
        </Panel>
        <Panel title={`Progress (${d.progress.length})`}>
          {d.progress.length === 0 ? <Empty /> : d.progress.map((p) => (
            <div key={p.topic_id} className="text-xs border-b border-zinc-800 py-2 flex justify-between">
              <span className="text-zinc-300">{p.topic_id}</span>
              <span className="text-zinc-500">{p.quiz_completed ? `quiz ${p.quiz_score}/${p.quiz_total}` : "studied"}</span>
            </div>
          ))}
        </Panel>
        <Panel title={`Payments (${d.payments.length})`}>
          {d.payments.length === 0 ? <Empty /> : d.payments.map((p) => (
            <div key={p.session_id} className="text-xs border-b border-zinc-800 py-2 flex justify-between">
              <span className="text-zinc-300">{p.plan} ${p.amount}</span>
              <span className={p.payment_status === "paid" ? "text-emerald-400" : "text-zinc-500"}>{p.payment_status}</span>
            </div>
          ))}
        </Panel>
      </div>
    </Modal>
  );
}

const Field = ({ l, children }) => <div><p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{l}</p>{children}</div>;
const Panel = ({ title, children }) => <div className="border border-zinc-800 rounded-lg p-3"><p className="text-[11px] uppercase tracking-wider text-gold mb-2">{title}</p><div className="max-h-44 overflow-y-auto">{children}</div></div>;
const Empty = () => <p className="text-xs text-zinc-600">none</p>;

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/users?q=${encodeURIComponent(q)}`).then((r) => setUsers(r.data.users)).catch(() => {});
  }, [q]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 max-w-md">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input data-testid="user-search" className={`${input} pl-9`} placeholder="search email or name" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>
      <div className="border border-zinc-800 rounded-lg overflow-hidden">
        {users.map((u) => {
          const lvl = levelFromXp(u.xp || 0);
          return (
            <button key={u.user_id} data-testid={`user-row-${u.user_id}`} onClick={() => setSel(u.user_id)} className="w-full text-left flex items-center gap-3 p-3 border-b border-zinc-800 last:border-0 hover:bg-zinc-900/60 transition-colors">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-zinc-200 flex items-center gap-2">{u.name} {u.is_admin && <Shield size={12} className="text-gold" />}</p>
                <p className="text-xs text-zinc-600">{u.email}</p>
              </div>
              {u.plan === "pro" ? <span className="text-[10px] uppercase tracking-wider text-yellow-400 border border-yellow-500/40 px-2 py-0.5 rounded flex items-center gap-1"><Crown size={11} /> pro</span> : <span className={tag}>free</span>}
              <span className="text-xs text-gold flex items-center gap-1"><Coins size={12} /> {u.gold ?? 0}</span>
              <span className="text-xs text-gem-light">Lv {lvl.level}</span>
              <ChevronRight size={15} className="text-zinc-600" />
            </button>
          );
        })}
        {users.length === 0 && <p className="text-sm text-zinc-600 p-4">No users found.</p>}
      </div>
      {sel && <UserDetail userId={sel} onClose={() => setSel(null)} onChanged={load} />}
    </div>
  );
}

// ---------------- CURRICULUM ----------------
const blankTopic = { tier: 0, order: 99, title: "", level: "intro", scope_summary: "", concepts: ["", "", "", ""], flashcards: [{ q: "", a: "" }, { q: "", a: "" }] };

function TopicForm({ topic, onClose, onSaved }) {
  const isNew = !topic.topic_id;
  const [f, setF] = useState({ ...blankTopic, ...topic, concepts: topic.concepts?.length ? [...topic.concepts] : blankTopic.concepts, flashcards: topic.flashcards?.length ? topic.flashcards.map(x => ({ ...x })) : blankTopic.flashcards });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f, tier: +f.tier, order: +f.order, concepts: f.concepts.filter(c => c.trim()), flashcards: f.flashcards.filter(c => c.q.trim()) };
      if (isNew) await api.post("/admin/topics", payload);
      else await api.put(`/admin/topics/${topic.topic_id}`, payload);
      toast.success(isNew ? "Topic created" : "Topic updated (quiz cache cleared)");
      onSaved?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Modal onClose={onClose} wide>
      <span className={label}>{isNew ? "new topic" : `edit · ${topic.topic_id}`}</span>
      <h3 className="font-serif font-bold text-2xl text-zinc-100 mt-1 mb-4">{isNew ? "Create topic" : "Edit topic"}</h3>
      <div className="space-y-3">
        <Field l="Title"><input data-testid="topic-title" className={input} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
        <div className="grid grid-cols-3 gap-3">
          <Field l="Tier (0-4)"><input type="number" className={input} value={f.tier} onChange={(e) => setF({ ...f, tier: e.target.value })} /></Field>
          <Field l="Order"><input type="number" className={input} value={f.order} onChange={(e) => setF({ ...f, order: e.target.value })} /></Field>
          <Field l="Level"><select className={input} value={f.level} onChange={(e) => setF({ ...f, level: e.target.value })}>{LEVELS.map(l => <option key={l}>{l}</option>)}</select></Field>
        </div>
        <Field l="Scope summary"><textarea className={`${textarea} min-h-[90px]`} value={f.scope_summary} onChange={(e) => setF({ ...f, scope_summary: e.target.value })} /></Field>
        <Field l="Key concepts (one per line)"><textarea className={`${textarea} min-h-[110px]`} value={f.concepts.join("\n")} onChange={(e) => setF({ ...f, concepts: e.target.value.split("\n") })} /></Field>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Flashcards</p>
          {f.flashcards.map((fc, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <input className={input} placeholder="question" value={fc.q} onChange={(e) => { const n = [...f.flashcards]; n[i] = { ...n[i], q: e.target.value }; setF({ ...f, flashcards: n }); }} />
              <input className={input} placeholder="answer" value={fc.a} onChange={(e) => { const n = [...f.flashcards]; n[i] = { ...n[i], a: e.target.value }; setF({ ...f, flashcards: n }); }} />
              <button onClick={() => setF({ ...f, flashcards: f.flashcards.filter((_, j) => j !== i) })} className="text-zinc-500 hover:text-rose-400"><X size={16} /></button>
            </div>
          ))}
          <button onClick={() => setF({ ...f, flashcards: [...f.flashcards, { q: "", a: "" }] })} className={btnGhost}><Plus size={14} /> Add flashcard</button>
        </div>
      </div>
      <div className="flex justify-end mt-5"><button data-testid="save-topic" onClick={save} disabled={saving} className={btnPrimary}><Save size={15} /> {saving ? "saving..." : "Save topic"}</button></div>
    </Modal>
  );
}

function CurriculumTab() {
  const [topics, setTopics] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => { api.get("/admin/topics").then((r) => setTopics(r.data.topics)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this topic?")) return;
    try { await api.delete(`/admin/topics/${id}`); toast.success("Topic deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const byTier = [0, 1, 2, 3, 4].map((t) => ({ tier: t, items: topics.filter((x) => x.tier === t) }));

  return (
    <div>
      <div className="flex justify-end mb-4"><button data-testid="new-topic" onClick={() => setEditing(blankTopic)} className={btnPrimary}><Plus size={15} /> New topic</button></div>
      {byTier.map((g) => (
        <div key={g.tier} className="mb-5">
          <p className={label}>Tier {g.tier}</p>
          <div className="mt-2 border border-zinc-800 rounded-lg overflow-hidden">
            {g.items.map((t) => (
              <div key={t.topic_id} className="flex items-center gap-3 p-3 border-b border-zinc-800 last:border-0 hover:bg-zinc-900/50">
                <span className="text-xs text-zinc-600 w-8">#{t.order}</span>
                <span className="text-sm text-zinc-200 flex-1">{t.title}</span>
                <span className={tag}>{t.level}</span>
                <button data-testid={`edit-topic-${t.topic_id}`} onClick={() => setEditing(t)} className="text-zinc-400 hover:text-gold"><Pencil size={15} /></button>
                <button onClick={() => del(t.topic_id)} className="text-zinc-400 hover:text-rose-400"><Trash2 size={15} /></button>
              </div>
            ))}
            {g.items.length === 0 && <p className="text-xs text-zinc-600 p-3">No topics in this tier.</p>}
          </div>
        </div>
      ))}
      {editing && <TopicForm topic={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

// ---------------- LESSONS ----------------
function LessonEditor({ slug, onClose, onSaved }) {
  const [f, setF] = useState(null);
  const [orig, setOrig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/admin/lessons/${slug}`).then((r) => { setF(r.data); setOrig(r.data); }).catch(() => toast.error("Could not load lesson"));
  }, [slug]);

  const save = async () => {
    if (f.markdown !== orig.markdown && f.tts === orig.tts &&
        !window.confirm("You changed the lesson text but not the TTS narration. The audio will no longer match. Save anyway?")) return;
    setSaving(true);
    try {
      await api.put(`/admin/lessons/${slug}`, { title: f.title, markdown: f.markdown, tts: f.tts || "" });
      toast.success("Lesson updated");
      onSaved?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!f) return <Modal onClose={onClose}><p className="text-gold font-mono"><span className="cursor-blink">loading</span></p></Modal>;

  return (
    <Modal onClose={onClose} wide>
      <span className={label}>edit · {slug}</span>
      <h3 className="font-serif font-bold text-2xl text-zinc-100 mt-1 mb-4">Edit lesson</h3>
      <div className="space-y-3">
        <Field l="Title"><input data-testid="lesson-title" className={input} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
        <Field l="Lesson (markdown)"><textarea data-testid="lesson-markdown" className={`${textarea} min-h-[300px] font-mono text-xs`} value={f.markdown} onChange={(e) => setF({ ...f, markdown: e.target.value })} /></Field>
        <Field l="TTS narration (plain text read aloud, keep in sync with the lesson)"><textarea data-testid="lesson-tts" className={`${textarea} min-h-[300px] font-mono text-xs`} value={f.tts || ""} onChange={(e) => setF({ ...f, tts: e.target.value })} /></Field>
      </div>
      <div className="flex justify-end mt-5"><button data-testid="save-lesson" onClick={save} disabled={saving} className={btnPrimary}><Save size={15} /> {saving ? "saving..." : "Save lesson"}</button></div>
    </Modal>
  );
}

function LessonsTab() {
  const [lessons, setLessons] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => { api.get("/admin/lessons").then((r) => setLessons(r.data.lessons)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const byTopic = [...new Set(lessons.map((l) => l.topic_id))].map((tid) => ({ tid, items: lessons.filter((l) => l.topic_id === tid) }));

  return (
    <div>
      {byTopic.map((g) => (
        <div key={g.tid} className="mb-5">
          <p className={label}>{g.tid}</p>
          <div className="mt-2 border border-zinc-800 rounded-lg overflow-hidden">
            {g.items.map((l) => (
              <div key={l.slug} className="flex items-center gap-3 p-3 border-b border-zinc-800 last:border-0 hover:bg-zinc-900/50">
                <span className="text-xs text-zinc-600 w-40 truncate">{l.slug}</span>
                <span className="text-sm text-zinc-200 flex-1">{l.title}</span>
                <button data-testid={`edit-lesson-${l.slug}`} onClick={() => setEditing(l.slug)} className="text-zinc-400 hover:text-gold"><Pencil size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      ))}
      {lessons.length === 0 && <p className="text-sm text-zinc-600">No lessons in the DB yet. They seed from lesson/*.md on server start.</p>}
      {editing && <LessonEditor slug={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

// ---------------- OVERVIEW ----------------
function OverviewTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <p className="text-gold font-mono"><span className="cursor-blink">loading</span></p>;
  const tiles = [
    { k: "users", v: s.users }, { k: "pro users", v: s.pro_users }, { k: "topics", v: s.topics },
    { k: "interviews", v: s.sessions }, { k: "graded answers", v: s.graded_turns },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-zinc-800 border border-zinc-800 rounded-lg overflow-hidden">
      {tiles.map((t) => (
        <div key={t.k} className="bg-zinc-950 p-5" data-testid={`admin-stat-${t.k.replace(" ", "-")}`}>
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">{t.k}</p>
          <p className="font-sans font-bold text-3xl text-gold glow-gold mt-1">{t.v}</p>
        </div>
      ))}
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  if (user === null) return null;
  if (!user || !user.is_admin) return <Navigate to="/" replace />;

  const TABS = [
    { id: "overview", label: "Overview", icon: <Shield size={15} /> },
    { id: "users", label: "Users", icon: <Users size={15} /> },
    { id: "curriculum", label: "Curriculum", icon: <BookOpen size={15} /> },
    { id: "lessons", label: "Lessons", icon: <ScrollText size={15} /> },
  ];

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <span className={label}>admin console</span>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">Keep of Records</h1>

        <div className="flex gap-2 mt-6 mb-8 border-b border-zinc-800">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.id ? "border-gold text-gold" : "border-transparent text-zinc-400 hover:text-zinc-200"}`}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && <OverviewTab />}
        {tab === "users" && <UsersTab />}
        {tab === "curriculum" && <CurriculumTab />}
        {tab === "lessons" && <LessonsTab />}
      </div>
    </div>
  );
}
